import { FormikProvider, useFormik } from 'formik';
import ReportForm, { FormValues, validationSchema } from '../components/ReportForm/ReportForm';
import { Modal } from '@round/whitelabel-ui';
import styles from './EditReportModal.module.css';
import { Button } from '@round/whitelabel-ui';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { showNotification } from 'helpers';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import { useUpdateReportMutation, useTeam, useUsers } from '@round/creatorbase-helpers/api-hooks';
import { UserOption } from 'Modules/Plans/User/hooks/useUsersSelect';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    report: creatorbase.Report | null;
};

export const EditReportModal = ({ isOpen, onClose, report }: Props) => {
    const currency = useCurrencies().options.find((c) => c.value === report?.currency_id);
    const { data: team } = useTeam(report?.team_id);
    const assigneeIds = report?.assignee_ids;
    const { data: assigneesData } = useUsers(
        { id: assigneeIds?.toString(), page_size: assigneeIds?.length },
        { enabled: !!assigneeIds?.length && isOpen }
    );

    const { update } = useUpdateReportMutation(report?.id);
    const formik = useFormik<FormValues>({
        initialValues: {
            name: report?.name || '',
            description: report?.description || '',
            team: team ? { value: team.id, label: team.name } : null,
            currency: currency ?? null,
            assignees:
                assigneesData?.results.map(
                    (a): UserOption => ({ value: a.id, label: a.name, is_creatorbase_admin: a.is_creatorbase_admin })
                ) ?? [],
            project_ids: report?.project_ids ?? [],
            image: report?.image || null,
        },
        validationSchema,
        onSubmit: async (values, helpers) => {
            if (!report?.id) {
                return;
            }

            try {
                await update({
                    id: report.id,
                    name: values.name,
                    description: values.description,
                    team_id: values.team?.value!,
                    currency_id: values.currency?.value!,
                    assignee_ids: values.assignees?.map((a) => a.value) ?? [],
                    ...(values.image instanceof File ? { image: values.image } : {}),
                });
                showNotification('Updated', 'info');
                helpers.resetForm();
                onClose();
            } catch (e) {
                if (
                    e instanceof Error &&
                    creatorbaseQueries.reports.utils.update.isExpectedErrorResponse(e) &&
                    e.response.status === 400
                ) {
                    helpers.setErrors(
                        mapApiErrorsToFormikErrors({
                            name: e.response.data.name,
                            description: e.response.data.description,
                            team: e.response.data.team_id,
                            currency: e.response.data.currency_id,
                            assignees: e.response.data.assignee_ids,
                            project_ids: e.response.data.project_ids,
                            image: e.response.data.image,
                        })
                    );
                    return;
                }
                showNotification('Could not update report', 'error');
            }
        },
        enableReinitialize: true,
    });

    const handleClose = () => {
        if (formik.isSubmitting) {
            return;
        }

        onClose();
        formik.resetForm();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            closeOnEscape={!formik.isSubmitting}
            closeOnOverlayClick={!formik.isSubmitting}
            className={styles.modal}
        >
            <Modal.Header>
                <Modal.Title>Edit Report</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <FormikProvider value={formik}>
                    <ReportForm disabledFields={{ team: !!report?.project_ids.length }} />
                </FormikProvider>
            </Modal.Body>

            <Modal.Actions>
                <Button
                    appearance="primary"
                    isLoading={formik.isSubmitting}
                    disabled={formik.isSubmitting || !formik.dirty || !formik.isValid}
                    onClick={formik.submitForm}
                >
                    Update
                </Button>

                <Button appearance="ghost" onClick={handleClose} disabled={formik.isSubmitting}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default EditReportModal;
